import {Box, Button, FormControlLabel, Switch, Tab, TextField, Typography} from "@mui/material";
import React, {ChangeEventHandler, SyntheticEvent, useContext, useEffect, useState} from "react";
import {PathwayUser, ProjectDetails, Projects} from "../../redux/types/firebase";
import CookbookTools from "./CookbookTools";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {ProjectTables} from "./Projects";
import Versions from "./Versions";
import {ErrorBoundary} from "@sentry/react";
import makeStyles from "@mui/styles/makeStyles";
import {TabContext, TabList, TabPanel} from "@mui/lab";

const useStyles = makeStyles(theme => ({
    content: {
        alignItems: "stretch",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        paddingBottom: theme.spacing(2)
    },
    details: {
        display: "flex",
        flexDirection:"column",
        flexGrow: 1,
        height: "100%",
        overflowY: "auto",
        paddingRight: 4,
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
    tabPanel: {
        display: "flex",
        flexDirection: "column",
        height: "calc(100% - 96px)",
        "&[hidden]": {
            display: "none"
        }
    }
}));

interface Props {
    onSetMode: (mode: string, project: string) => void
    onUpdate: (project: string, field: string, value: unknown) => void
    project: string
    projects: Projects
    user: PathwayUser
}
export default function ProjectDetailsEditor({onSetMode, onUpdate, project, projects, user}: Props) {
    const classes = useStyles();
    const [deleteUnlocked, setDeleteUnlocked] = useState(false);
    const [projectDetails, setProjectDetails] = useState<ProjectDetails>();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [tab, setTab] = useState("details");
    const {remove} = useContext(FirebaseContext);
    const setTop = Boolean(projects[project]?.setTop);
    const onClickEdit = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        onSetMode("graph", event.currentTarget.dataset.project as string);
    };
    const onClickIntertitles = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        onSetMode("intertitle", event.currentTarget.dataset.project as string);
    };
    const onClickPlay = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        onSetMode("play", event.currentTarget.dataset.project as string);
    };
    const onBlurProjectField = (project: string, field: string) =>
        (event: React.FocusEvent<HTMLInputElement>) => {
            onUpdate(project, field, event.target.value);
        };
    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        onUpdate(project, "filterUnconnected", event.target.checked);
    const handleProjectChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        onUpdate(project, "cookbook", event.target.checked);
    const handleSetTopChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        onUpdate(project, "setTop", event.target.checked);
    const handleDeleteProjectClick = () => {
        Promise.all(ProjectTables.map(table =>
            remove(`${table}/${project}`))).then(() => setDeleteUnlocked(false));
    };
    const handleDescriptionChange: ChangeEventHandler<HTMLInputElement> = ({target}) =>
        setDescription(target.value);
    const handleNameChange: ChangeEventHandler<HTMLInputElement> = ({target}) =>
        setName(target.value);
    const handleTabChange = (event: SyntheticEvent, value: any) => setTab(value);

    useEffect(() => {
        if (projectDetails && projects[project] === undefined) {
            setProjectDetails(undefined);
            setName("");
            setDescription("");
        } else if (projectDetails !== projects[project]) {
            setProjectDetails(projects[project]);
            setName(projects[project].name || "");
            setDescription(projects[project].description || "");
        }
    }, [project, projects, projectDetails]);
    
    if (! projectDetails) return null;
    return (
        <Box className={classes.content}>
            <ErrorBoundary>
                <Box display="flex">
                    <Typography title={projectDetails.description} sx={{flexGrow: 1}} variant="h6">
                        {projectDetails.name}
                    </Typography>
                    <Button data-project={project} onClick={onClickEdit} title="Edit graph"  variant="contained"
                            sx={{m:1}}>
                        Edit
                    </Button>
                    {setTop ?
                        <Button data-project={project} onClick={onClickPlay} title="Play set top game" variant="contained"
                                sx={{m:1}}>
                            Play
                        </Button> :
                        <Button data-project={project} onClick={onClickIntertitles} title="Show intertitles" 
                                    variant="contained" sx={{m:1}}>
                            Intertitles
                        </Button>
                    }
                    <Button data-project={project} onClick={() => onSetMode("script", project)} title="Show script"
                            variant="contained" sx={{m:1}}>
                        Script
                    </Button>
                </Box>
                <TabContext value={tab}>
                    <TabList onChange={handleTabChange}>
                        <Tab label="Details" value="details"/>
                        <Tab label="Versions" value="versions"/>
                        <Tab label="Tools" value="tools"/>
                    </TabList>
                    <TabPanel className={classes.tabPanel} value="details">
                        <Box className={classes.details}>
                            <TextField label="Name" value={name} onBlur={onBlurProjectField(project, "name")}
                                       onChange={handleNameChange} size="small" sx={{m:1}}/>
                            <TextField label="Description" value={description}
                                       onBlur={onBlurProjectField(project, "description")}
                                       onChange={handleDescriptionChange}  size="small" sx={{m:1}}/>
                        </Box>
                    </TabPanel>
                    <TabPanel className={classes.tabPanel} value="versions">
                        <Versions project={project}/>
                    </TabPanel>
                    <TabPanel className={classes.tabPanel} value="tools">
                        {user.roles.admin &&
                            <Box m={1}>
                                <Box>
                                    <FormControlLabel control={
                                        <Switch checked={projectDetails.setTop === true} onChange={handleSetTopChange}/>
                                    } label="Set top box game"/>
                                </Box>
                                <Box>
                                    <FormControlLabel control={
                                        <Switch checked={Boolean(projectDetails.cookbook)} onChange={handleProjectChange}/>
                                    } label="Cookbook Project"/>
                                    {projectDetails.cookbook &&
                                        <Box>
                                            <FormControlLabel control={
                                                <Switch checked={Boolean(projectDetails.filterUnconnected)}
                                                        onChange={handleFilterChange}/>
                                            } label="Filter Unconnected States" title={"When selected, states that " +
                                            "cannot be reached will be excluded from the graph"}/>
                                            <CookbookTools project={project}/>
                                        </Box>
                                    }
                                </Box>
                                <Switch color="error" checked={deleteUnlocked} onChange={
                                    () => setDeleteUnlocked(! deleteUnlocked)
                                }/>
                                <Button color="error" disabled={! deleteUnlocked} onClick={handleDeleteProjectClick}
                                        sx={{marginLeft:1}} variant="contained">
                                    Delete Project
                                </Button>
                            </Box>}
                    </TabPanel>
                </TabContext>
            </ErrorBoundary>
        </Box>
    );
}
